export type NodeId =
  | "mund"
  | "speiseroehre"
  | "magen"
  | "leber"
  | "gallenblase"
  | "bauchspeicheldruese"
  | "zwoelffingerdarm"
  | "duenndarm"
  | "dickdarm"
  | "enddarm";

export interface GraphEdge {
  from: NodeId;
  to: NodeId;
}

// Traversal order used for player-assignment (contiguous slices).
export const ALL_NODES: NodeId[] = [
  "mund",
  "speiseroehre",
  "magen",
  "leber",
  "gallenblase",
  "bauchspeicheldruese",
  "zwoelffingerdarm",
  "duenndarm",
  "dickdarm",
  "enddarm",
];

// Bolus-Fortschrittspfad (ohne Drüsen-Zuflüsse).
export const MAIN_PATH: NodeId[] = [
  "mund",
  "speiseroehre",
  "magen",
  "zwoelffingerdarm",
  "duenndarm",
  "dickdarm",
  "enddarm",
];

export const EDGES: GraphEdge[] = [
  { from: "mund", to: "speiseroehre" },
  { from: "speiseroehre", to: "magen" },
  { from: "magen", to: "zwoelffingerdarm" },
  { from: "leber", to: "gallenblase" },
  { from: "gallenblase", to: "zwoelffingerdarm" },
  { from: "bauchspeicheldruese", to: "zwoelffingerdarm" },
  { from: "zwoelffingerdarm", to: "duenndarm" },
  { from: "duenndarm", to: "dickdarm" },
  { from: "dickdarm", to: "enddarm" },
];

export const GLANDS: ReadonlyArray<NodeId> = ["leber", "gallenblase", "bauchspeicheldruese"];

export function predecessors(node: NodeId): NodeId[] {
  return EDGES.filter((e) => e.to === node).map((e) => e.from);
}

export function successors(node: NodeId): NodeId[] {
  return EDGES.filter((e) => e.from === node).map((e) => e.to);
}

/**
 * Active window for a bolus position:
 *   { current } ∪ direct predecessors ∪ direct successors
 *   ∪ glands whose direct successor is already in the set (only if current is magen or zwoelffingerdarm)
 *
 * Gland propagation is localized to the gland junction (zwoelffingerdarm) and its
 * immediate predecessor (magen) to show realistic Galle-Vorbereitung.
 */
export function activeWindow(current: NodeId): Set<NodeId> {
  const set = new Set<NodeId>([current]);
  for (const p of predecessors(current)) set.add(p);
  for (const s of successors(current)) set.add(s);

  // Only propagate glands if at the gland junction (zwoelffingerdarm) or approaching it (magen)
  if (current === "magen" || current === "zwoelffingerdarm") {
    let added = true;
    while (added) {
      added = false;
      for (const gland of GLANDS) {
        if (set.has(gland)) continue;
        for (const succ of successors(gland)) {
          if (set.has(succ)) {
            set.add(gland);
            added = true;
            break;
          }
        }
      }
    }
  }
  return set;
}
